/**
 * @voltxampere/shared — Fuzzy scoring helper (Sprint 22 A1)
 *
 * Implements BLUEPRINT §14.2's five-tier ranking strategy used by
 * the Cmd+K command palette, the sidebar search, and any other
 * surface that needs "type a few characters, see the most relevant
 * candidates first" semantics.
 *
 * Tier               Bonus                 Notes
 * ─────────────────  ────────────────────  ────────────────────────
 * 1. Exact match     1000                  Whole query equals whole target
 * 2. Prefix match    500 + len bonus       target.startsWith(query); bonus
 *                                          = round(query.length / target.length × 100)
 * 3. Word boundary   +300 per match        Match position is index 0 OR
 *                                          target[i-1] is a boundary char
 *                                          (" ", "-", "_", ".", "·", "/", ":")
 * 4. Contiguous run  +100 × group length   For groups whose length ≥ 2
 * 5. Scattered char  +10 per char          Singleton matches (group length = 1)
 *
 * Penalty: −5 per unmatched character of `target` (so a long target
 * with a tiny query loses to a tighter target). Penalty applies on
 * tiers 3-5 only — exact and prefix matches keep their full bonus
 * because the caller already sees they consumed the entire query.
 *
 * Score interpretation:
 *   - 0  → no match (some query character not found in target)
 *   - >0 → match; higher is more relevant
 *
 * Why a separate fuzzy.ts when @voltxampere/shared already exports
 * `levenshtein`? Levenshtein measures "how far apart are these two
 * strings". Fuzzy ranking measures "does query approximately appear
 * inside target, and how cleanly". They optimise for different
 * questions; the assertion evaluator wants the former, the palette
 * wants the latter.
 *
 * Case sensitivity: default insensitive (the palette UX surfaces
 * `file.save` when the user types "SAVE"). Pass `caseSensitive: true`
 * for identifier-style searches where capitalisation is load-bearing.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/** Boundary characters that earn the +300 word-boundary bonus. */
const BOUNDARY_CHARS = new Set([" ", "-", "_", ".", "·", "/", ":"]);
const NO_MATCH = Object.freeze({ score: 0, matchedIndices: [] });
/**
 * Score how well `query` matches `target`. Returns 0 when any query
 * character cannot be found in left-to-right order.
 */
export function fuzzyScore(query, target, options = {}) {
    if (query.length === 0 || target.length === 0)
        return NO_MATCH;
    const caseSensitive = options.caseSensitive ?? false;
    const q = caseSensitive ? query : query.toLowerCase();
    const t = caseSensitive ? target : target.toLowerCase();
    // Tier 1 — exact match.
    if (q === t) {
        return {
            score: 1000,
            matchedIndices: rangeIndices(target.length),
        };
    }
    // Tier 2 — prefix match. Note: only when query is shorter than
    // target (a longer query that starts with target is still tier 1
    // material above).
    if (t.startsWith(q)) {
        const lengthBonus = Math.round((q.length / t.length) * 100);
        return {
            score: 500 + lengthBonus,
            matchedIndices: rangeIndices(q.length),
        };
    }
    // Tiers 3-5 — greedy left-to-right scan of query chars across
    // target. Bail out the moment a query char cannot be located.
    const matchedIndices = [];
    let cursor = 0;
    for (const qc of q) {
        const found = t.indexOf(qc, cursor);
        if (found === -1)
            return NO_MATCH;
        matchedIndices.push(found);
        cursor = found + 1;
    }
    // All query chars placed. Compute tier 3-5 score.
    let groupBonus = 0;
    let scatteredBonus = 0;
    let boundaryBonus = 0;
    let groupStart = 0;
    for (const [i, idx] of matchedIndices.entries()) {
        // Word-boundary check.
        if (idx === 0) {
            boundaryBonus += 300;
        }
        else {
            const prev = target[idx - 1];
            if (prev !== undefined && BOUNDARY_CHARS.has(prev.toLowerCase())) {
                boundaryBonus += 300;
            }
        }
        // End of contiguous group? Either we are at the last matched
        // index, or the next matched index is not adjacent.
        const next = matchedIndices[i + 1];
        const groupBroken = next === undefined || next !== idx + 1;
        if (groupBroken) {
            const groupLength = i - groupStart + 1;
            if (groupLength >= 2) {
                groupBonus += groupLength * 100;
            }
            else {
                scatteredBonus += 10;
            }
            groupStart = i + 1;
        }
    }
    const unmatchedCount = target.length - matchedIndices.length;
    const penalty = unmatchedCount * 5;
    const total = boundaryBonus + groupBonus + scatteredBonus - penalty;
    if (total <= 0)
        return NO_MATCH;
    return { score: total, matchedIndices };
}
/**
 * Convenience helper: rank a list of items by their `getKey()` text
 * against `query` and return only those that scored above 0,
 * descending. Stable on ties.
 *
 * Callers can re-sort by category, recency, etc. on the returned
 * array — Array#sort is stable in modern JS so additional passes
 * preserve the fuzzy ordering for items that tie on the secondary
 * key.
 */
export function fuzzyRank(query, items, getKey, options = {}) {
    if (query.length === 0)
        return items.map((item) => ({ item, match: NO_MATCH }));
    const out = [];
    for (const item of items) {
        const match = fuzzyScore(query, getKey(item), options);
        if (match.score > 0)
            out.push({ item, match });
    }
    out.sort((a, b) => b.match.score - a.match.score);
    return out;
}
function rangeIndices(n) {
    const arr = new Array(n);
    for (let i = 0; i < n; i++)
        arr[i] = i;
    return arr;
}
//# sourceMappingURL=fuzzy.js.map